import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { homePath } from '@/auth/landing';
import { canOpenPage, type GatedPage } from '@/auth/pageKeys';
import { takeLanding } from '@/auth/rolePages';
import { useSession } from '@/auth/SessionProvider';
import { ApiProvider } from '@/provider/ApiProvider';

export { permissionsLoaded } from '@/auth/session';

/** How long the gate waits for the permission map before it decides anyway (fail closed). */
export const PAGE_GATE_WAIT_MS = 10_000;

/**
 * What a route gate renders when it refuses the page: /nopermission, except on a role's first
 * landing (auth/rolePages.ts `markLanding`: after a switch, a sign-in, a preview start or end,
 * a page load that adopted another role), which goes to the role's home instead. One shot: the
 * marker is consumed here, so a page the user opens later still shows /nopermission.
 */
export const RefusedRedirect = () => {
  const { pathname } = useLocation();
  const [target] = useState(() => {
    if (!takeLanding(pathname)) return '/nopermission';
    const home = homePath(ApiProvider.returnUserData()?.home_module ?? null);
    return home.split('?')[0] === pathname ? '/nopermission' : home;
  });
  return <Navigate to={target} replace />;
};

/**
 * Route-level page gate (authorization part 7g): the page mounts, and fires its requests,
 * only when the active role may open it (`canOpenPage`); otherwise /nopermission (the role's
 * home on its first landing, `RefusedRedirect`).
 *
 * It decides only once the session holds the active role's permission map: while it is missing
 * (a hard refresh, sign-in or role switch still loading it) the gate shows `fallback` and does
 * not redirect. It follows the session (`useSession`), so it opens as soon as the map arrives.
 * The redirect is a render of the gated route itself, so it never acts on a navigation away
 * from the page.
 */
export const PageGate = ({
  page,
  children,
  fallback = null,
}: {
  page: GatedPage;
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const { permissionsLoaded: loaded } = useSession();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (loaded) return undefined;
    const timer = setTimeout(() => setTimedOut(true), PAGE_GATE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [loaded]);

  if (!loaded && !timedOut) return <>{fallback}</>;
  return canOpenPage(page) ? <>{children}</> : <RefusedRedirect />;
};
